import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { usePricing } from '@/context/PricingContext';
import { toast } from 'sonner';
import { CheckCircle2, Sparkles } from 'lucide-react';

import StepProgress from './StepProgress';
import BuilderNav from './BuilderNav';
import LiveBasket from './LiveBasket';
import StepFoundation from './steps/StepFoundation';
import StepBoltOns from './steps/StepBoltOns';
import StepBrief from './steps/StepBrief';
import StepCareHosting from './steps/StepCareHosting';
import StepReview, { validate as validateCustomer } from './steps/StepReview';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'uidesigns_builder_state_v1';
const TOTAL_STEPS = 5;

const EMPTY_STATE = {
  tier: null,
  boltOns: [],
  brief: '',
  carePlan: null,
  skipCare: false,
  hosting: null,
  skipHosting: false,
};

export default function ProjectBuilder() {
  const pricing = usePricing();
  const pb = pricing.project_builder || {};
  const tiers = pb.tiers || [];
  const boltOns = pb.bolt_ons || [];
  const carePlans = pb.care_plans || [];
  const hostingTiers = pb.hosting_tiers || [];
  const warnings = pb.no_hosting_warnings || [];
  const chips = pb.brief_helper_chips || [];

  const [step, setStep] = useState(0);
  const [state, setState] = useState(EMPTY_STATE);
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [basketPulse, setBasketPulse] = useState(0);
  const progressRef = useRef(null);
  const isFirstRender = useRef(true);

  // Smooth-scroll the progress bar to the top of the viewport whenever the step changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!progressRef.current) return;
    const NAV_OFFSET = 88; // ~80px nav + a touch of breathing room
    const top = progressRef.current.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
  }, [step]);

  // Hydrate from localStorage once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.state) setState({ ...EMPTY_STATE, ...saved.state });
        if (saved.customer) setCustomer(saved.customer);
        if (typeof saved.step === 'number') setStep(Math.min(saved.step, TOTAL_STEPS - 1));
      }
    } catch { /* ignore */ }
  }, []);

  // Auto-save
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, customer, step }));
    } catch { /* ignore */ }
  }, [state, customer, step]);

  // Default hosting tier = first free option
  useEffect(() => {
    if (!state.hosting && hostingTiers.length > 0) {
      setState(prev => ({ ...prev, hosting: hostingTiers[0] }));
    }
  }, [hostingTiers, state.hosting]);

  const pulse = () => setBasketPulse(x => x + 1);

  /* -------- selection handlers -------- */
  const selectTier = useCallback((t) => {
    setState(prev => ({ ...prev, tier: t }));
    pulse();
    // Auto-advance to Step 2 after tier confirmation for a smoother flow
    setStep(s => (s === 0 ? 1 : s));
  }, []);

  const toggleBoltOn = useCallback((b) => {
    setState(prev => {
      const has = prev.boltOns.find(x => x.id === b.id);
      let nextBoltOns, nextBoosts = { ...prev.boosts };
      if (has) {
        nextBoltOns = prev.boltOns.filter(x => x.id !== b.id);
        delete nextBoosts[b.id];
      } else {
        nextBoltOns = [...prev.boltOns, b];
        // Auto-select first boost pack (Starter) when adding a boost-enabled bolt-on
        if (b.has_boost && Array.isArray(b.boost_options) && b.boost_options.length > 0) {
          nextBoosts[b.id] = b.boost_options[0];
        }
      }
      return { ...prev, boltOns: nextBoltOns, boosts: nextBoosts };
    });
    pulse();
  }, []);

  const selectBoost = useCallback((boltId, pack) => {
    setState(prev => ({ ...prev, boosts: { ...prev.boosts, [boltId]: pack } }));
    pulse();
  }, []);

  const setBrief = useCallback((text) => setState(prev => ({ ...prev, brief: text })), []);

  const selectCare = useCallback((p) => {
    setState(prev => ({ ...prev, carePlan: p, skipCare: false })); pulse();
  }, []);
  const skipCare = useCallback((v) => setState(prev => ({
    ...prev,
    skipCare: v,
    carePlan: v ? null : prev.carePlan,
    // Hosting is bundled with care plans — if no care plan, hosting is also skipped
    skipHosting: v ? true : prev.skipHosting,
  })), []);

  const selectHosting = useCallback((h) => {
    setState(prev => ({ ...prev, hosting: h, skipHosting: false })); pulse();
  }, []);

  /* -------- totals -------- */
  const setupTotal = useMemo(() => {
    const base = state.tier ? Number(state.tier.price) || 0 : 0;
    const addons = state.boltOns.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
    return base + addons;
  }, [state.tier, state.boltOns]);

  const monthlyTotal = useMemo(() => {
    const care = state.carePlan && !state.skipCare ? Number(state.carePlan.price_monthly) || 0 : 0;
    const host = state.hosting && !state.skipHosting ? Number(state.hosting.monthly_surcharge) || 0 : 0;
    const boosts = Object.values(state.boosts || {})
      .reduce((sum, p) => sum + (Number(p?.price_monthly) || 0), 0);
    return care + host + boosts;
  }, [state.carePlan, state.skipCare, state.hosting, state.skipHosting, state.boosts]);

  // Care-plan is forced when ANY care-required bolt-on is in the basket.
  const careRequiredByBoltOn = useMemo(
    () => state.boltOns.some(b => b.requires_care),
    [state.boltOns],
  );

  /* -------- step gating -------- */
  const canAdvance = useMemo(() => {
    if (step === 0) return !!state.tier;
    // Block leaving Step 2 (bolt-ons) if a care-required power-up is selected
    // but no care plan is chosen yet — force them onto Step 4 to pick one.
    if (step === 1 && careRequiredByBoltOn && !state.carePlan) {
      // Allow continuing — they'll be forced to choose at Step 4
      return true;
    }
    if (step === 3) {
      if (careRequiredByBoltOn) return !!state.carePlan;  // can't skip
      return state.skipCare || !!state.carePlan;
    }
    if (step === 4) {
      const v = validateCustomer(customer);
      return v.all && agreed && !!state.tier && (!careRequiredByBoltOn || !!state.carePlan);
    }
    return true;
  }, [step, state.tier, state.carePlan, state.skipCare, customer, agreed, careRequiredByBoltOn]);

  /* -------- submit -------- */
  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        name: customer.name.trim(),
        email: customer.email.trim(),
        phone: customer.phone.trim(),
        brief: state.brief.trim(),
        agreed_to_terms: agreed,
        selections: {
          tier: state.tier ? { id: state.tier.id, name: state.tier.name, price: state.tier.price, tagline: state.tier.tagline } : null,
          bolt_ons: state.boltOns.map(b => {
            const boost = state.boosts?.[b.id];
            return {
              id: b.id,
              name: b.name,
              price: b.price,
              price_suffix: b.price_suffix || '',
              requires_care: !!b.requires_care,
              boost: boost ? {
                id: boost.id,
                name: boost.name,
                price_monthly: boost.price_monthly,
                credits: boost.credits,
                interactions: boost.interactions,
              } : null,
            };
          }),
          care_plan: state.carePlan ? { id: state.carePlan.id, name: state.carePlan.name, price_monthly: state.carePlan.price_monthly } : null,
          hosting: state.hosting ? { id: state.hosting.id, name: state.hosting.name, ram: state.hosting.ram, monthly_surcharge: state.hosting.monthly_surcharge } : null,
          skip_hosting: state.skipHosting,
          skip_care: state.skipCare,
        },
        totals: { setup_total: setupTotal, monthly_total: monthlyTotal },
      };
      const res = await fetch(`${API}/api/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      const data = await res.json();
      setSubmitted(data);
      localStorage.removeItem(STORAGE_KEY);
      toast.success('Quote sent — check your inbox!');
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setState(EMPTY_STATE);
    setCustomer({ name: '', email: '', phone: '' });
    setAgreed(false);
    setStep(0);
    setSubmitted(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  /* -------- success screen -------- */
  if (submitted) {
    return (
      <div data-testid="builder-success" className="max-w-2xl mx-auto px-4 py-20 sm:py-28 text-center animate-[fadeIn_0.5s_ease]">
        <div className="w-16 h-16 rounded-2xl bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <h1 className="font-['Outfit'] text-3xl sm:text-5xl font-bold text-[#0A0A0A] dark:text-white mb-3">Quote on its way</h1>
        <p className="text-base text-slate-400 dark:text-slate-400 mb-2 max-w-md mx-auto">Thanks {customer.name.split(' ')[0]} — your custom quote has been emailed to <strong className="text-[#0A0A0A] dark:text-white">{customer.email}</strong>.</p>
        <p className="text-sm text-slate-500 dark:text-slate-500 mb-6">I'll personally review it and reply within 24 hours.</p>
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400 dark:text-slate-600 mb-8">Ref {submitted.reference}</p>
        <button
          type="button"
          data-testid="build-another"
          onClick={reset}
          className="inline-flex items-center gap-2 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-[#0A0A0A] dark:text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-slate-200 dark:hover:bg-white/10 hover:border-slate-300 dark:hover:border-white/20 transition-all"
        >
          <Sparkles className="w-4 h-4" />
          Build another
        </button>
      </div>
    );
  }

  /* -------- step render -------- */
  const renderStep = () => {
    switch (step) {
      case 0: return <StepFoundation tiers={tiers} selected={state.tier} onSelect={selectTier} />;
      case 1: return (
        <StepBoltOns
          boltOns={boltOns}
          selectedIds={state.boltOns.map(b => b.id)}
          onToggle={toggleBoltOn}
          boosts={state.boosts}
          onSelectBoost={selectBoost}
          hasCarePlanSelected={!!state.carePlan}
        />
      );
      case 2: return <StepBrief brief={state.brief} onChange={setBrief} chips={chips} />;
      case 3: return (
        <StepCareHosting
          carePlans={carePlans}
          hostingTiers={hostingTiers}
          warnings={warnings}
          carePlan={state.carePlan}
          skipCare={state.skipCare}
          onSelectCare={selectCare}
          onSkipCare={skipCare}
          hosting={state.hosting}
          onSelectHosting={selectHosting}
          forcedByBoltOns={careRequiredByBoltOn}
          forcedReason={state.boltOns.filter(b => b.requires_care).map(b => b.name)}
        />
      );
      case 4: return (
        <StepReview
          state={state}
          setupTotal={setupTotal}
          monthlyTotal={monthlyTotal}
          customer={customer}
          setCustomer={setCustomer}
          agreed={agreed}
          setAgreed={setAgreed}
        />
      );
      default: return null;
    }
  };

  return (
    <div data-testid="project-builder" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28 lg:pb-12">
      {/* Header — minimal, confident */}
      <div className="max-w-3xl mb-10 sm:mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] backdrop-blur-sm mb-6">
          <span className="relative flex items-center justify-center w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-[#00E5FF] animate-ping opacity-75" />
            <span className="relative w-1.5 h-1.5 rounded-full bg-[#00E5FF]" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-700 dark:text-slate-300 font-semibold">Project Builder · Live</span>
        </div>

        <h1 className="font-['Outfit'] text-4xl sm:text-6xl lg:text-7xl font-black text-[#0A0A0A] dark:text-white tracking-tight leading-[0.95] mb-4">
          Build your<br className="sm:hidden" />
          <span className="sm:ml-3 text-[#00E5FF]">custom quote.</span>
        </h1>

        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 font-medium max-w-md">
          Live pricing. Emailed within the hour. Zero sales calls.
        </p>
      </div>

      {/* Progress rail */}
      <div ref={progressRef} className="mb-10 sm:mb-12 scroll-mt-24">
        <StepProgress step={step} onJump={(i) => setStep(i)} />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 lg:gap-10">
        <div className="min-w-0">
          {renderStep()}
          <BuilderNav
            step={step}
            maxStep={TOTAL_STEPS - 1}
            onBack={() => setStep(s => Math.max(0, s - 1))}
            onNext={() => {
              if (step < TOTAL_STEPS - 1) setStep(s => s + 1);
              else submit();
            }}
            nextDisabled={!canAdvance}
            submitting={submitting}
            nextLabel={step < TOTAL_STEPS - 1 ? 'Continue' : 'Generate & Send My Quote'}
          />
        </div>

        <LiveBasket state={state} setupTotal={setupTotal} monthlyTotal={monthlyTotal} pulseKey={basketPulse} />
      </div>
    </div>
  );
}
